/**
 * 时间计算核心模块
 * 修正版本 - 实现正确的双重计费系统
 * 
 * 核心原则：
 * 1. 加班时间：按连续时间计算，包含午休时间
 * 2. 调休时间：按工作时间计算，扣除午休时间
 */

/**
 * 格式化日期时间字符串
 * @param {Date|string} date - 日期对象或字符串
 * @returns {string} 格式化后的日期时间字符串
 */
export function formatDateTime(date) {
  try {
    const d = new Date(date);
    if (isNaN(d.getTime())) {
      return "日期格式错误";
    }
    
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  } catch (error) {
    return "日期格式错误";
  }
}

/**
 * 计算加班时间（连续时间计算）
 * 
 * 加班时间按实际连续时间计算，不扣除午休时间
 * 例如：9:00-18:00 = 9小时（包含午休）
 * 
 * @param {Date|string} startTime - 开始时间
 * @param {Date|string} endTime - 结束时间
 * @returns {number} 加班小时数
 */
export function calculateOvertimeHours(startTime, endTime) {
  const start = new Date(startTime);
  const end = new Date(endTime);
  
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return 0;
  }
  
  if (end <= start) {
    return 0;
  }
  
  // 计算总时间（毫秒）
  const totalMilliseconds = end.getTime() - start.getTime();
  
  // 转换为小时，保留2位小数
  const totalHours = totalMilliseconds / (1000 * 60 * 60);
  
  return Math.round(totalHours * 100) / 100;
}

/**
 * 计算实际时间差（不扣除午休，按起止时间的实际差值计算）
 * 这是 calculateOvertimeHours 的别名，用于摆烂/年假的"按实际时间计算"选项
 *
 * @param {Date|string} startTime - 开始时间
 * @param {Date|string} endTime - 结束时间
 * @returns {number} 实际小时数
 */
export function calculateActualHours(startTime, endTime) {
  return calculateOvertimeHours(startTime, endTime);
}

/**
 * 计算工作时间（调休时间计算）
 * 
 * 调休时间按工作日计算，扣除午休时间
 * 例如：9:00-18:00 = 8小时（扣除1小时午休）
 * 
 * @param {Date|string} startTime - 开始时间
 * @param {Date|string} endTime - 结束时间
 * @returns {number} 工作小时数
 */
export function calculateWorkingHours(startTime, endTime) {
  const start = new Date(startTime);
  const end = new Date(endTime);
  
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return 0;
  }
  
  if (end <= start) {
    return 0;
  }
  
  let totalHours = 0;
  let currentDay = new Date(start);
  
  // 按天计算工作时间
  while (currentDay < end) {
    const dayEnd = new Date(currentDay);
    dayEnd.setHours(18, 0, 0, 0);  // 当天最晚下班时间18:00

    const dayStart = new Date(currentDay);
    dayStart.setHours(9, 0, 0, 0);   // 当天最早上班时间9:00

    // 计算当天的有效工作时间段
    const effectiveStart = new Date(Math.max(currentDay.getTime(), dayStart.getTime()));
    const effectiveEnd = new Date(Math.min(end.getTime(), dayEnd.getTime()));

    if (effectiveEnd > effectiveStart) {
      // 计算当天的工作小时数
      let dayHours = (effectiveEnd.getTime() - effectiveStart.getTime()) / (1000 * 60 * 60);

      // 检查是否跨越午休时间，扣除实际重叠的时间
      const lunchStart = new Date(currentDay);
      lunchStart.setHours(12, 0, 0, 0);
      const lunchEnd = new Date(currentDay);
      lunchEnd.setHours(13, 0, 0, 0);

      // 计算与午休的实际重叠时间
      if (effectiveStart < lunchEnd && effectiveEnd > lunchStart) {
        const overlapStart = new Date(Math.max(effectiveStart.getTime(), lunchStart.getTime()));
        const overlapEnd = new Date(Math.min(effectiveEnd.getTime(), lunchEnd.getTime()));
        const overlapHours = (overlapEnd.getTime() - overlapStart.getTime()) / (1000 * 60 * 60);
        dayHours = Math.max(0, dayHours - overlapHours);
      }

      // 累加当天的工作时间
      totalHours += dayHours;
    }

    // 移动到下一天的开始
    currentDay.setDate(currentDay.getDate() + 1);
    currentDay.setHours(0, 0, 0, 0);  // 设置为当天的0点，而不是9点
  }
  
  return Math.round(totalHours * 100) / 100;
}

/**
 * 计算摆烂时间
 *
 * 摆烂时间的计算规则：
 * - 只计算工作时间段（9:00-18:00）
 * - 扣除午休时间（12:00-13:00）
 * - 每天最多8小时工作时间
 * - 例如：2025/12/12 09:00 到 2025/12/13 18:00 = 16小时（两天各8小时）
 *
 * @param {Date|string} startTime - 开始时间
 * @param {Date|string} endTime - 结束时间
 * @returns {number} 摆烂小时数
 */
export function calculateRestHours(startTime, endTime) {
  const start = new Date(startTime);
  const end = new Date(endTime);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return 0;
  }

  if (end <= start) {
    return 0;
  }

  // 摆烂时间只计算工作时间段（9:00-18:00），扣除午休
  let totalHours = 0;
  let currentDay = new Date(start);
  currentDay.setHours(0, 0, 0, 0);

  // 按天计算
  while (currentDay < end) {
    // 当天的工作时间边界
    const workStart = new Date(currentDay);
    workStart.setHours(9, 0, 0, 0);
    const workEnd = new Date(currentDay);
    workEnd.setHours(18, 0, 0, 0);

    // 午休时间
    const lunchStart = new Date(currentDay);
    lunchStart.setHours(12, 0, 0, 0);
    const lunchEnd = new Date(currentDay);
    lunchEnd.setHours(13, 0, 0, 0);

    // 计算用户请求与当天工作时间的交集
    const effectiveStart = new Date(Math.max(start.getTime(), workStart.getTime()));
    const effectiveEnd = new Date(Math.min(end.getTime(), workEnd.getTime()));

    if (effectiveEnd > effectiveStart) {
      // 计算当天的工作时间
      let dayHours = (effectiveEnd.getTime() - effectiveStart.getTime()) / (1000 * 60 * 60);

      // 计算与午休时间的重叠并扣除
      const lunchOverlapStart = new Date(Math.max(effectiveStart.getTime(), lunchStart.getTime()));
      const lunchOverlapEnd = new Date(Math.min(effectiveEnd.getTime(), lunchEnd.getTime()));

      if (lunchOverlapEnd > lunchOverlapStart) {
        const lunchOverlapHours = (lunchOverlapEnd.getTime() - lunchOverlapStart.getTime()) / (1000 * 60 * 60);
        dayHours -= lunchOverlapHours;
      }

      totalHours += Math.max(0, dayHours);
    }

    // 移动到下一天
    currentDay.setDate(currentDay.getDate() + 1);
  }

  return Math.round(totalHours * 100) / 100;
}

/**
 * 计算年假小时数
 *
 * @param {number} days - 年假天数
 * @returns {number} 年假小时数（每天8小时）
 */
export function calculateAnnualLeaveHours(days) {
  return days * 8;
}

/**
 * 计算年假天数
 * 
 * @param {number} hours - 年假小时数
 * @returns {number} 年假天数
 */
export function calculateAnnualLeaveDays(hours) {
  return Math.round((hours / 8) * 100) / 100;
}

/**
 * 验证时间范围
 * 
 * @param {Date|string} startTime - 开始时间
 * @param {Date|string} endTime - 结束时间
 * @returns {Object} 验证结果
 */
export function validateTimeRange(startTime, endTime) {
  const start = new Date(startTime);
  const end = new Date(endTime);
  
  const result = {
    isValid: true,
    errors: []
  };
  
  if (isNaN(start.getTime())) {
    result.isValid = false;
    result.errors.push('开始时间格式无效');
  }
  
  if (isNaN(end.getTime())) {
    result.isValid = false;
    result.errors.push('结束时间格式无效');
  }
  
  if (result.isValid && end <= start) {
    result.isValid = false;
    result.errors.push('结束时间必须晚于开始时间');
  }
  
  return result;
}

/**
 * 获取时间计算规则说明
 * 
 * @returns {Object} 规则说明对象
 */
export function getCalculationRules() {
  return {
    overtime: {
      title: '加班时间计算规则',
      description: '按连续时间计算，包含午休时间',
      examples: [
        { input: '9:00-18:00', output: '9小时', note: '连续9小时，包含午休' },
        { input: '9:00-20:00', output: '11小时', note: '连续11小时，包含午休' },
        { input: '14:00-22:00', output: '8小时', note: '连续8小时' }
      ],
      formula: '加班时间 = 结束时间 - 开始时间'
    },
    rest: {
      title: '调休时间计算规则',
      description: '按工作时间计算，扣除午休时间',
      examples: [
        { input: '9:00-18:00', output: '8小时', note: '9小时减去1小时午休' },
        { input: '9:00-17:00', output: '7小时', note: '8小时减去1小时午休' },
        { input: '14:00-18:00', output: '4小时', note: '4小时，无午休时间' }
      ],
      formula: '调休时间 = 工作时间 - 午休时间（如有）',
      workingHours: '标准工作时间：9:00-18:00（12:00-13:00为午休时间）'
    },
    annualLeave: {
      title: '年假计算规则',
      description: '年假按每天8小时计算',
      examples: [
        { input: '1天', output: '8小时' },
        { input: '0.5天', output: '4小时' },
        { input: '2.5天', output: '20小时' }
      ],
      formula: '年假小时数 = 年假天数 × 8'
    }
  };
}

/**
 * 比较两种计算方式的差异
 * 
 * @param {Date|string} startTime - 开始时间
 * @param {Date|string} endTime - 结束时间
 * @returns {Object} 比较结果
 */
export function compareCalculationMethods(startTime, endTime) {
  const overtimeHours = calculateOvertimeHours(startTime, endTime);
  const workingHours = calculateWorkingHours(startTime, endTime);
  
  return {
    startTime: formatDateTime(startTime),
    endTime: formatDateTime(endTime),
    overtime: {
      hours: overtimeHours,
      description: '加班时间（连续时间）'
    },
    working: {
      hours: workingHours,
      description: '调休时间（工作时间）'
    },
    difference: {
      hours: Math.round((overtimeHours - workingHours) * 100) / 100,
      description: '差异（通常为午休时间）'
    }
  };
}
